import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, Loader2, MessageCircle, Minus, Package, Plus, ShoppingBag, Tag } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CartDrawer } from "../components/CartDrawer";
import { ProductCard } from "../components/ProductCard";
import { ShareButton } from "../components/ShareButton";
import { FavoriteButton } from "../components/FavoriteButton";
import { api, assetUrl } from "../lib/api";
import { STORE, waLink, formatGs, sectorMeta } from "../data/store";
import { useCart } from "../context/CartContext";

export default function ProductPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { add, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const [qty, setQty] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setStatus("loading"); setQty(1);
    api.getProductByCode(code)
      .then(async (p) => {
        setProduct(p); setStatus("ok");
        document.title = `${p.name} — ${formatGs(p.price)} | ${STORE.name}`;
        const same = await api.listProducts({ sector: p.sector });
        setRelated(same.filter((x) => x.code !== p.code).slice(0, 4));
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch(() => setStatus("notfound"));
    return () => { document.title = `${STORE.name} — ${STORE.slogan}`; };
  }, [code]);

  const onNav = (k, v) => navigate("/", { state: { nav: k, value: v } });

  const addToCart = () => {
    add(product, qty);
    toast.success(`${qty} × ${product.name} agregado al pedido`);
    setOpen(true);
  };

  if (status === "loading")
    return <div className="min-h-screen flex items-center justify-center text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  if (status === "notfound")
    return (
      <div className="App">
        <Header search={search} setSearch={(v) => { setSearch(v); onNav("search", v); }} onNav={onNav} />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
          <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h1 className="font-display text-2xl font-bold text-gray-900">Producto no encontrado</h1>
          <p className="text-gray-500 mt-2">El código <span className="font-mono-code">{code}</span> no existe en el catálogo.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0A3977] text-white font-bold"><ArrowLeft className="w-4 h-4" /> Volver al catálogo</Link>
        </div>
        <Footer />
      </div>
    );

  const meta = sectorMeta(product.sector);
  const inStock = product.stock > 0;
  const msg = `Hola ${STORE.name}, consulto por el producto [Código: ${product.code}] ${product.name} de ${formatGs(product.price)}. ¿Está disponible?`;

  return (
    <div className="App">
      <Header search={search} setSearch={(v) => { setSearch(v); onNav("search", v); }} onNav={onNav} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="product-page">
        <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-gray-900">Inicio</Link>
          <ChevronRight className="w-4 h-4" />
          <button onClick={() => onNav(product.sector)} className="hover:text-gray-900">{meta.label}</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900 font-semibold truncate">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 shadow-lg aspect-square lg:sticky lg:top-24">
            <img data-testid="product-page-image" src={assetUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
            <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold border" style={{ background: meta.bg, color: meta.text, borderColor: meta.border }}>{meta.label}</span>
            {product.featured && <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold bg-[#F59E0B] text-white">Destacado</span>}
          </div>

          <div>
            <div className="flex items-center gap-3 text-sm">
              <span className="font-mono-code font-bold text-gray-400" data-testid="product-page-code">{product.code}</span>
              <span className="text-gray-300">·</span>
              <span className="font-semibold text-gray-600">{product.brand}</span>
              {product.category && (<><span className="text-gray-300">·</span><span className="inline-flex items-center gap-1 text-gray-500"><Tag className="w-3.5 h-3.5" /> {product.category}</span></>)}
            </div>
            <h1 data-testid="product-page-name" className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tight text-gray-900 leading-tight">{product.name}</h1>
            <p className="mt-4 text-gray-600 text-base leading-relaxed">{product.description}</p>
            {product.specs && (
              <div className="mt-4 flex flex-wrap gap-2">
                {product.specs.split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">{s}</span>
                ))}
              </div>
            )}

            <div className="mt-6 p-5 rounded-2xl border border-gray-200 bg-[#FAFAFA]">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-gray-500">Precio · {product.unit}</div>
                  <div data-testid="product-page-price" className="font-display font-black text-4xl tracking-tight text-[#E02424]">{formatGs(product.price)}</div>
                </div>
                <span data-testid="product-page-stock" className={`inline-flex items-center gap-1 text-sm font-semibold ${inStock ? "text-green-600" : "text-gray-400"}`}>
                  <Package className="w-4 h-4" /> {inStock ? `Disponible (${product.stock})` : "Sin stock"}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center border border-gray-300 rounded-full bg-white">
                  <button data-testid="product-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 hover:bg-gray-100 rounded-l-full"><Minus className="w-4 h-4" /></button>
                  <span data-testid="product-qty" className="w-10 text-center font-bold">{qty}</span>
                  <button data-testid="product-qty-inc" onClick={() => setQty((q) => q + 1)} className="p-2.5 hover:bg-gray-100 rounded-r-full"><Plus className="w-4 h-4" /></button>
                </div>
                <button data-testid="product-page-add" onClick={addToCart}
                  className="flex-1 min-w-[180px] inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0A3977] text-white font-bold hover:bg-[#0067B1] transition-colors">
                  <ShoppingBag className="w-4 h-4" /> Agregar al pedido
                </button>
              </div>
              <div className="mt-3 flex gap-3">
                <a data-testid="product-page-whatsapp" href={waLink(msg)} target="_blank" rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white font-bold hover:bg-[#1EBE5B] transition-colors">
                  <MessageCircle className="w-4 h-4" /> Consultar por WhatsApp
                </a>
                <ShareButton product={product} />
                <FavoriteButton product={product} className="w-11 h-11 border border-gray-300" />
              </div>
            </div>

            <p className="mt-4 text-xs text-gray-400">Retiro en tienda: {STORE.address}, {STORE.city}. {STORE.hours}.</p>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16" data-testid="related-products">
            <div className="text-xs font-bold tracking-widest uppercase text-[#E02424]">También te puede interesar</div>
            <h2 className="font-display text-2xl font-bold text-gray-900 mt-1 mb-6">Más productos de {meta.label}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <CartDrawer />
    </div>
  );
}
